import React, { useState, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import Underline from '@tiptap/extension-underline';
import { Printer, Upload, Settings, FileText, Bold, Italic, List, ListOrdered, Table as TableIcon } from 'lucide-react';
import api from '@/api/api';
import './ManualCertificatePrint.css';
import { formatDate } from '../../utils/dateTimeUtils';

const ManualCertificatePrint = () => {
  const [formData, setFormData] = useState({
    heading: '',
    certificate_no: '',
    date: new Date().toISOString().split('T')[0],
  });
  
  const [settings, setSettings] = useState({
    college_name: '',
    college_address: '',
    principal_name: '',
    signature_url: '',
    stamp_url: ''
  });
  
  const [showSettings, setShowSettings] = useState(false);
  const [loading, setLoading] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Table,
      TableRow,
      TableHeader,
      TableCell,
    ],
    content: '<p>Start writing your certificate content here...</p>',
    editorProps: {
      attributes: {
        class: 'prose prose-sm sm:prose lg:prose-lg xl:prose-2xl mx-auto focus:outline-none min-h-[300px] p-4',
      },
    },
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/certificates/settings');
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const handleImageUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const { readFileSmart } = await import('@/utils/imageCompressor');
      const result = await readFileSmart(file, { photoMaxDim: 800, quality: 0.85 });
      setSettings(prev => ({ ...prev, [`${type}_url`]: result.data }));
    } catch (err) {
      console.error('File read failed:', err);
    }
  };

  const handleSaveSettings = async () => {
    setLoading(true);
    try {
      await api.post('/certificates/settings', settings);
      alert('Settings saved successfully!');
      setShowSettings(false);
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = async (mode) => {
    if (!formData.heading || !editor.getHTML()) {
      alert('Please fill in heading and body');
      return;
    }

    // Save certificate to database before printing
    try {
      await api.post('/certificates/save', {
        heading: formData.heading,
        certificate_no: formData.certificate_no,
        date: formData.date,
        body_html: editor.getHTML(),
        print_mode: mode,
        signature_url: settings.signature_url,
        stamp_url: settings.stamp_url
      });
    } catch (error) {
      console.error('Error saving certificate:', error);
      // Continue with print even if save fails
    }

    const printContent = generatePrintContent(mode);
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  const generatePrintContent = (mode) => {
    const showHeader = mode === 'normal';

    return `
    <!DOCTYPE html>
    <html>
    <head>
      <title>${formData.heading}</title>
      <style>
        @page {
          size: A4;
          margin: 25mm;
        }
        @media print {
          body { margin: 0; }
          .no-print { display: none; }
        }
        body {
          font-family: 'Times New Roman', Times, serif;
          line-height: 1.6;
          color: #000;
        }
        .certificate-container {
          max-width: 210mm;
          margin: 0 auto;
          padding: 20px;
        }
        .college-header {
          text-align: center;
          margin-bottom: 30px;
          border-bottom: 2px solid #333;
          padding-bottom: 15px;
        }
        .college-name {
          font-size: 24px;
          font-weight: bold;
          text-transform: uppercase;
          margin-bottom: 5px;
        }
        .college-address {
          font-size: 14px;
          color: #666;
        }
        .certificate-header {
          text-align: center;
          margin: 30px 0;
        }
        .certificate-heading {
          font-size: 22px;
          font-weight: bold;
          text-decoration: underline;
          margin-bottom: 15px;
        }
        .certificate-meta {
          display: flex;
          justify-content: space-between;
          font-size: 12px;
          margin-bottom: 20px;
        }
        .certificate-body {
          text-align: justify;
          font-size: 14px;
          margin: 30px 0;
          min-height: 200px;
        }
        .certificate-body table {
          width: 100%;
          border-collapse: collapse;
          margin: 15px 0;
        }
        .certificate-body table td,
        .certificate-body table th {
          border: 1px solid #333;
          padding: 8px;
          text-align: left;
        }
        .certificate-body table th {
          background-color: #f5f5f5;
          font-weight: bold;
        }
        .certificate-body p {
          margin: 10px 0;
        }
        .certificate-body strong {
          font-weight: bold;
        }
        .certificate-body em {
          font-style: italic;
        }
        .certificate-body u {
          text-decoration: underline;
        }
        .certificate-footer {
          margin-top: 60px;
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
        }
        .signature-block {
          text-align: center;
        }
        .signature-image {
          width: 120px;
          height: 60px;
          margin-bottom: 5px;
        }
        .stamp-image {
          width: 80px;
          height: 80px;
          position: absolute;
          left: 50%;
          transform: translateX(-50%);
          margin-top: -40px;
        }
        .principal-name {
          font-weight: bold;
          border-top: 1px solid #333;
          padding-top: 5px;
          margin-top: 10px;
        }
      </style>
    </head>
    <body>
      <div class="certificate-container">
        ${showHeader ? `
          <div class="college-header">
            <div class="college-name">${settings.college_name || 'College Name'}</div>
            <div class="college-address">${settings.college_address || 'College Address'}</div>
          </div>
        ` : ''}

        <div class="certificate-header">
          <div class="certificate-heading">${formData.heading}</div>
        </div>

        <div class="certificate-meta">
          <div><strong>Certificate No:</strong> ${formData.certificate_no}</div>
          <div><strong>Date:</strong> ${formatDate(formData.date)}</div>
        </div>

        <div class="certificate-body">
          ${editor.getHTML()}
        </div>

        <div class="certificate-footer">
          <div class="signature-block">
            ${settings.stamp_url ? `<img src="${settings.stamp_url}" class="stamp-image" alt="Stamp" />` : ''}
          </div>
          <div class="signature-block">
            ${settings.signature_url ? `<img src="${settings.signature_url}" class="signature-image" alt="Signature" />` : '<div style="height: 60px;"></div>'}
            <div class="principal-name">${settings.principal_name || 'Principal/Director'}</div>
            <div style="font-size: 12px;">Principal/Director</div>
          </div>
        </div>
      </div>

      <div class="no-print" style="position: fixed; top: 10px; right: 10px; padding: 10px; background: white; border: 1px solid #ccc; border-radius: 5px;">
        <button onclick="window.print()" style="padding: 8px 16px; background: #2563eb; color: white; border: none; border-radius: 4px; cursor: pointer;">Print Now</button>
      </div>
    </body>
    </html>
    `;
  };

  const insertTable = () => {
    editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  };

  if (!editor) {
    return null;
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Manual Certificate Print</h1>
          <p className="text-gray-600 mt-1">Create and print custom certificates</p>
        </div>
        <button
          onClick={() => setShowSettings(!showSettings)}
          className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
        >
          <Settings className="h-5 w-5" />
          Settings
        </button>
      </div>

      {showSettings && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold mb-4">Certificate Settings</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">College Name</label>
              <input
                type="text"
                value={settings.college_name}
                onChange={(e) => setSettings({...settings, college_name: e.target.value})}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Enter college name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">College Address</label>
              <input
                type="text"
                value={settings.college_address}
                onChange={(e) => setSettings({...settings, college_address: e.target.value})}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Enter college address"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Principal/Director Name</label>
              <input
                type="text"
                value={settings.principal_name}
                onChange={(e) => setSettings({...settings, principal_name: e.target.value})}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Enter principal name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Signature Image</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleImageUpload(e, 'signature')}
                className="w-full px-4 py-2 border rounded-lg"
              />
              {settings.signature_url && (
                <img src={settings.signature_url} alt="Signature" className="mt-2 h-16 border" />
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Stamp Image</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleImageUpload(e, 'stamp')}
                className="w-full px-4 py-2 border rounded-lg"
              />
              {settings.stamp_url && (
                <img src={settings.stamp_url} alt="Stamp" className="mt-2 h-16 border" />
              )}
            </div>
          </div>
          <button
            onClick={handleSaveSettings}
            disabled={loading}
            className="mt-4 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400"
          >
            {loading ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      )}

      <div className="bg-white rounded-lg shadow p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Certificate Heading *</label>
            <input
              type="text"
              value={formData.heading}
              onChange={(e) => setFormData({...formData, heading: e.target.value})}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., Character Certificate"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Certificate Number *</label>
            <input
              type="text"
              value={formData.certificate_no}
              onChange={(e) => setFormData({...formData, certificate_no: e.target.value})}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., CERT/2025/001"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Date *</label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({...formData, date: e.target.value})}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Certificate Body * (Rich Text Editor)</label>
          
          {/* Toolbar */}
          <div className="border rounded-t-lg bg-gray-50 p-2 flex flex-wrap gap-2">
            <button
              onClick={() => editor.chain().focus().toggleBold().run()}
              className={`p-2 rounded ${editor.isActive('bold') ? 'bg-blue-200' : 'hover:bg-gray-200'}`}
              title="Bold"
            >
              <Bold className="h-4 w-4" />
            </button>
            <button
              onClick={() => editor.chain().focus().toggleItalic().run()}
              className={`p-2 rounded ${editor.isActive('italic') ? 'bg-blue-200' : 'hover:bg-gray-200'}`}
              title="Italic"
            >
              <Italic className="h-4 w-4" />
            </button>
            <button
              onClick={() => editor.chain().focus().toggleUnderline().run()}
              className={`p-2 rounded ${editor.isActive('underline') ? 'bg-blue-200' : 'hover:bg-gray-200'}`}
              title="Underline"
            >
              <span className="text-sm font-bold underline">U</span>
            </button>
            <div className="w-px bg-gray-300 mx-1"></div>
            <button
              onClick={() => editor.chain().focus().toggleBulletList().run()}
              className={`p-2 rounded ${editor.isActive('bulletList') ? 'bg-blue-200' : 'hover:bg-gray-200'}`}
              title="Bullet List"
            >
              <List className="h-4 w-4" />
            </button>
            <button
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
              className={`p-2 rounded ${editor.isActive('orderedList') ? 'bg-blue-200' : 'hover:bg-gray-200'}`}
              title="Numbered List"
            >
              <ListOrdered className="h-4 w-4" />
            </button>
            <div className="w-px bg-gray-300 mx-1"></div>
            <button
              onClick={insertTable}
              className="p-2 rounded hover:bg-gray-200"
              title="Insert Table"
            >
              <TableIcon className="h-4 w-4" />
            </button>
            
            {/* Table Controls - Show only when cursor is inside table */}
            {editor.isActive('table') && (
              <>
                <div className="w-px bg-gray-300 mx-1"></div>
                <button
                  onClick={() => editor.chain().focus().addRowBefore().run()}
                  className="px-2 py-1 text-xs rounded hover:bg-gray-200 border border-gray-300"
                  title="Add Row Above"
                >
                  + Row ↑
                </button>
                <button
                  onClick={() => editor.chain().focus().addRowAfter().run()}
                  className="px-2 py-1 text-xs rounded hover:bg-gray-200 border border-gray-300"
                  title="Add Row Below"
                >
                  + Row ↓
                </button>
                <button
                  onClick={() => editor.chain().focus().deleteRow().run()}
                  className="px-2 py-1 text-xs rounded hover:bg-red-100 border border-red-300 text-red-700"
                  title="Delete Row"
                >
                  − Row
                </button>
                <div className="w-px bg-gray-300 mx-1"></div>
                <button
                  onClick={() => editor.chain().focus().addColumnBefore().run()}
                  className="px-2 py-1 text-xs rounded hover:bg-gray-200 border border-gray-300"
                  title="Add Column Left"
                >
                  + Col ←
                </button>
                <button
                  onClick={() => editor.chain().focus().addColumnAfter().run()}
                  className="px-2 py-1 text-xs rounded hover:bg-gray-200 border border-gray-300"
                  title="Add Column Right"
                >
                  + Col →
                </button>
                <button
                  onClick={() => editor.chain().focus().deleteColumn().run()}
                  className="px-2 py-1 text-xs rounded hover:bg-red-100 border border-red-300 text-red-700"
                  title="Delete Column"
                >
                  − Col
                </button>
                <div className="w-px bg-gray-300 mx-1"></div>
                <button
                  onClick={() => editor.chain().focus().deleteTable().run()}
                  className="px-2 py-1 text-xs rounded hover:bg-red-100 border border-red-300 text-red-700 font-bold"
                  title="Delete Entire Table"
                >
                  Delete Table
                </button>
              </>
            )}
          </div>

          {/* Editor */}
          <div className="border border-t-0 rounded-b-lg">
            <EditorContent editor={editor} />
          </div>
          
          <p className="text-xs text-gray-500 mt-2">
            💡 <strong>Table Tips:</strong> Click "Insert Table" to add a 3x3 table. When cursor is inside the table, additional buttons appear to:
            <br/>• Add rows above/below • Delete rows • Add columns left/right • Delete columns • Delete entire table
          </p>
        </div>

        <div className="flex gap-3 pt-4">
          <button
            onClick={() => handlePrint('letterhead')}
            className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Printer className="h-5 w-5" />
            Print on Letterhead
          </button>
          <button
            onClick={() => handlePrint('normal')}
            className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            <FileText className="h-5 w-5" />
            Print on Normal Page
          </button>
        </div>
      </div>
    </div>
  );
};

export default ManualCertificatePrint;
